import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { HouseholdRetirementService, type ReadinessCardResponse } from '../../services/household-retirement-service';
import { money, monthYear } from '../retirement/format';
import { NavigateFn, SectionCard } from './shared';

/**
 * Optional dashboard card "Retirement readiness" (issue #32). Shown only
 * when the household has opted into the household plan; reads the compact
 * GET /retirement/household/readiness (deterministic, no Monte Carlo).
 * Separate from the #31 Plan card and its nightly portfolio check.
 */
export const RetirementReadinessCard: React.FC<{ onNavigate: NavigateFn; data?: ReadinessCardResponse | null }> = ({ onNavigate, data }) => {
  const [fetched, setFetched] = useState<ReadinessCardResponse | null>(null);
  const resp = data !== undefined ? data : fetched;
  useEffect(() => {
    if (data !== undefined) return;
    let live = true;
    HouseholdRetirementService.readiness().then((r) => { if (live) setFetched(r); }).catch(() => undefined);
    return () => { live = false; };
  }, [data]);
  if (!resp?.configured || !resp.readiness) return null;
  const r = resp.readiness;
  const cur = resp.currency ?? 'CAD';
  return (
    <SectionCard title="Retirement readiness" subtitle={`Household plan v${resp.model_version} · retire ${monthYear(r.household_retirement)}`}
      action={{ label: 'Household plan', link: { to: '/fire', search: { tab: 'household' } } }} onNavigate={onNavigate}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip size="small" color={r.on_track ? 'success' : 'warning'} label={r.on_track ? 'On track' : 'Gap'} variant="outlined" />
        <Typography variant="body2" className="fw-num">
          {money(r.projected, cur)} projected vs {money(r.required, cur)} required (today's $)
        </Typography>
      </Box>
      {(resp.at_65 ?? []).map((a) => (
        <Typography key={a.person_id} variant="body2" sx={{ mt: 0.5 }}>
          {a.name} at 65: net worth {money(a.net_worth, cur)}, investable {money(a.investable_real, cur)} in today's dollars
        </Typography>
      ))}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--text-secondary)' }}>{resp.disclaimer}</Typography>
    </SectionCard>
  );
};
